/**
 * @fileoverview Détail paiement backoffice.
 *
 * Vue de consultation d'une transaction : statuts indépendants, lignes LIQ,
 * historique de traitement et action de relance protégée par capacité.
 *
 * La relance STI est une tâche sensible avec motif obligatoire : elle s'ouvre
 * dans un `Sheet` pour garder le contexte du paiement accessible sans
 * surcharger la page détail.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Amount,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataCard,
  DataTable,
  EmptyState,
  LiquidationNumber,
  OperatorLogo,
  PageHeader,
  ReasonTextarea,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusBadge,
  Timeline,
  type TimelineItem,
  type TimelineItemTone,
} from '@gnpc/ui';
import { ArrowLeft, FileText, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  ADMIN_PAYMENTS,
  formatDateTime,
  maskPhone,
  type PaymentLineItem,
} from '../data/backoffice.ts';
import { CapabilityGuard } from '../features/auth/CapabilityProvider.tsx';

const lineColumns: ColumnDef<PaymentLineItem>[] = [
  {
    accessorKey: 'liquidationNumber',
    header: 'Code de paiement',
    cell: ({ row }) => <LiquidationNumber value={row.original.liquidationNumber} copyable />,
  },
  { accessorKey: 'plate', header: 'Plaque' },
  { accessorKey: 'amount', header: 'Montant', cell: ({ row }) => <Amount value={row.original.amount} /> },
  {
    accessorKey: 'sgiStatus',
    header: 'Enregistrement Quipux',
    cell: ({ row }) => <StatusBadge kind="sgi" status={row.original.sgiStatus} compact />,
  },
  {
    id: 'failureReason',
    header: 'Motif d’échec',
    cell: ({ row }) =>
      row.original.failureReason ? (
        <div className="max-w-[16rem]">
          <code className="text-status-failed-800 font-mono text-[11px]">
            {row.original.failureCode}
          </code>
          <p className="text-muted-foreground text-xs leading-4">{row.original.failureReason}</p>
        </div>
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      ),
  },
];

function getSgiTimelineTone(status: string): TimelineItemTone {
  if (status === 'RECORDED') return 'success';
  if (status === 'FAILED') return 'danger';
  if (status === 'PENDING' || status === 'PARTIAL') return 'warning';
  return 'neutral';
}

function isReplayableLine(line: PaymentLineItem | null): boolean {
  return line?.sgiStatus === 'FAILED' || line?.sgiStatus === 'PENDING';
}

export function PaymentDetailPage(): JSX.Element {
  const { paymentId } = useParams();
  const payment = useMemo(
    () => ADMIN_PAYMENTS.find((item) => item.id === paymentId),
    [paymentId],
  );
  const [reason, setReason] = useState('Timeout Quipux après débit confirmé.');
  const [lineDetailSheetOpen, setLineDetailSheetOpen] = useState(false);
  const [selectedLineNumber, setSelectedLineNumber] = useState<string | null>(null);

  const timelineItems = useMemo<TimelineItem[]>(
    () => {
      if (!payment) return [];

      return [
        {
          id: 'created',
          title: 'Paiement créé',
          timestamp: formatDateTime(payment.createdAt),
          description: `Référence ${payment.merchantReference}`,
          tone: 'info',
        },
        {
          id: 'confirmed',
          title: payment.paymentStatus === 'PAID' ? 'Paiement confirmé' : 'Statut paiement mis à jour',
          timestamp: formatDateTime(payment.updatedAt),
          meta: <StatusBadge kind="payment" status={payment.paymentStatus} compact />,
          tone: payment.paymentStatus === 'PAID' ? 'success' : 'warning',
        },
        {
          id: 'sgi',
          title:
            payment.sgiStatus === 'FAILED'
              ? 'Enregistrement chez Quipux à reprendre'
              : 'Enregistrement chez Quipux mis à jour',
          timestamp: formatDateTime(payment.updatedAt),
          description:
            payment.sgiStatus === 'FAILED'
              ? 'Une ou plusieurs lignes doivent être relancées.'
              : 'Les lignes sont suivies dans le statut d’enregistrement Quipux.',
          meta: <StatusBadge kind="sgi" status={payment.sgiStatus} compact />,
          tone: getSgiTimelineTone(payment.sgiStatus),
        },
      ];
    },
    [payment],
  );

  if (!payment) {
    return (
      <EmptyState
        title="Paiement introuvable"
        description="La référence demandée n'est pas disponible."
        action={
          <Button asChild variant="outline">
            <Link to="/payments">Retour aux paiements</Link>
          </Button>
        }
      />
    );
  }

  const replayableLines = payment.lineItems.filter(
    (line) => line.sgiStatus === 'FAILED' || line.sgiStatus === 'PENDING',
  );
  const selectedLine =
    payment.lineItems.find((line) => line.liquidationNumber === selectedLineNumber) ?? null;
  const selectedLineCanReplay = isReplayableLine(selectedLine);

  // Rejeu ciblé d'UNE ligne en échec (C-15) — complémentaire du rejeu global.
  function replayLine(line: PaymentLineItem): void {
    toast.success('Rejeu de ligne programmé', {
      description: `${line.liquidationNumber} — enregistrement Quipux relancé.`,
    });
  }

  return (
    <>
      <Sheet>
        {/* `grid-cols-1` (= minmax(0,1fr)) et non la colonne `auto` implicite :
          * empêche un enfant à large max-content (section synthèse, table) de
          * faire déborder toute la page à largeur contrainte (tablette). */}
        <div className="grid grid-cols-1 gap-6">
          <PageHeader
            eyebrow="Détail paiement"
            title={payment.merchantReference}
            description="Consulter le paiement, ses lignes de paiement et l'historique de traitement."
            actions={
              <>
                <Button
                  variant="outline"
                  onClick={() =>
                    toast.success('Justificatif de débit', {
                      description: `Ouverture du justificatif du paiement ${payment.merchantReference}.`,
                    })
                  }
                >
                  <FileText className="size-4" />
                  Justificatif de débit
                </Button>
                <Button asChild variant="outline">
                  <Link to="/payments">
                    <ArrowLeft className="size-4" />
                    Retour
                  </Link>
                </Button>
              </>
            }
          />

          {/* 5 cases — bordures gérées par un pattern uniforme (border-l sur
            * les cases paires en sm, sur toutes sauf la 1re en xl) pour
            * rester correct quel que soit le wrap responsive. */}
          <section
            aria-label="Synthèse du paiement"
            className="border-border grid overflow-hidden border-y sm:grid-cols-2 xl:grid-cols-5 [&>div]:border-border [&>div]:border-b [&>div:last-child]:border-b-0 xl:[&>div]:border-b-0 sm:[&>div:nth-child(even)]:border-l xl:[&>div]:border-l xl:[&>div:first-child]:border-l-0"
          >
            <div className="flex min-h-24 flex-col justify-center gap-2 px-4 py-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">Statut paiement</p>
              <div><StatusBadge kind="payment" status={payment.paymentStatus} /></div>
            </div>
            <div className="flex min-h-24 flex-col justify-center gap-2 px-4 py-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">Enregistrement chez Quipux</p>
              <div><StatusBadge kind="sgi" status={payment.sgiStatus} /></div>
            </div>
            <div className="flex min-h-24 flex-col justify-center gap-2 px-4 py-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">Montant total</p>
              <div className="text-quipux-navy-950 text-xl font-semibold tabular-nums">
                <Amount value={payment.totalAmount} />
              </div>
              <p className="text-muted-foreground text-xs">
                {payment.lineItems.length} lignes de paiement
              </p>
            </div>
            <div className="flex min-h-24 flex-col justify-center gap-2 px-4 py-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">Opérateur</p>
              {/* L'opérateur effectif alimente `idFormaPago` chez Quipux —
                * information clé pour l'agent lors d'un rejeu d'enregistrement. */}
              <OperatorLogo code={payment.operatorCode} withLabel />
              {payment.operatorReference ? (
                <p className="text-muted-foreground font-mono text-xs">
                  Réf. opérateur : {payment.operatorReference}
                </p>
              ) : null}
            </div>
            <div className="flex min-h-24 flex-col justify-center gap-2 px-4 py-4">
              <p className="text-muted-foreground text-xs font-medium uppercase">Téléphone</p>
              <p className="text-quipux-navy-950 font-mono text-lg font-semibold">
                {maskPhone(payment.userPhone)}
              </p>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Card className="gap-4">
              <CardHeader>
                <CardTitle>Lignes de paiement</CardTitle>
                <CardDescription>Plaque {payment.plate}</CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={lineColumns}
                  data={payment.lineItems}
                  rowAction={{
                    label: (line) => `Ouvrir le détail du code de paiement ${line.liquidationNumber}`,
                    onSelect: (line) => {
                      setSelectedLineNumber(line.liquidationNumber);
                      setLineDetailSheetOpen(true);
                    },
                  }}
                  mobileCardLabel="Lignes de paiement"
                  mobileCard={(line) => (
                    <DataCard
                      eyebrow={<LiquidationNumber value={line.liquidationNumber} copyable />}
                      title={line.plate}
                      aside={<Amount value={line.amount} />}
                      meta={<StatusBadge kind="sgi" status={line.sgiStatus} compact />}
                    />
                  )}
                  emptyState={<EmptyState title="Aucune ligne" description="Cette transaction ne contient aucun code de paiement." />}
                />
              </CardContent>
            </Card>

            <div className="grid content-start gap-4">
              <Card className="gap-4">
                <CardHeader>
                  <CardTitle>Historique de traitement</CardTitle>
                  <CardDescription>Étapes visibles par l'agent.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Timeline items={timelineItems} compact ariaLabel="Historique de traitement du paiement" />
                </CardContent>
              </Card>

              <CapabilityGuard capability="payment:replay">
                <SheetTrigger asChild>
                  <Button variant="destructive" disabled={replayableLines.length === 0}>
                    <RotateCcw className="size-4" />
                    Relancer l'enregistrement Quipux
                  </Button>
                </SheetTrigger>
              </CapabilityGuard>
            </div>
          </section>
        </div>

        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Relancer l'enregistrement Quipux</SheetTitle>
            <SheetDescription>
              Renseigner un motif avant de relancer les lignes en échec.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 p-5">
            <div className="rounded-lg border border-border p-4 text-sm">
              <p className="text-muted-foreground text-xs uppercase">Paiement</p>
              <p className="mt-1 font-mono text-quipux-navy-950">{payment.merchantReference}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">Plaque</p>
                  <p className="mt-1 font-medium">{payment.plate}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total</p>
                  <p className="mt-1">
                    <Amount value={payment.totalAmount} />
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-danger/25 bg-danger/5 p-4 text-sm">
              <p className="font-medium text-quipux-navy-950">Lignes à relancer</p>
              {replayableLines.length > 0 ? (
                <div className="mt-3 grid gap-2">
                  {replayableLines.map((line) => (
                    <div
                      key={line.liquidationNumber}
                      className="grid gap-2 rounded-md border border-border bg-card px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <LiquidationNumber value={line.liquidationNumber} copyable />
                          <p className="text-muted-foreground mt-1 text-xs">{line.plate}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <Amount value={line.amount} />
                          <div className="mt-1">
                            <StatusBadge kind="sgi" status={line.sgiStatus} compact />
                          </div>
                        </div>
                      </div>
                      {line.failureReason ? (
                        <p className="text-muted-foreground text-xs leading-4">
                          <code className="text-status-failed-800 font-mono">
                            {line.failureCode}
                          </code>
                          {' — '}
                          {line.failureReason}
                        </p>
                      ) : null}
                      {/* Rejeu ciblé de CETTE ligne (C-15), en plus du rejeu global. */}
                      <CapabilityGuard capability="payment:replay">
                        <Button
                          size="sm"
                          variant="outline"
                          className="justify-self-start"
                          onClick={() => replayLine(line)}
                        >
                          <RotateCcw className="size-3.5" />
                          Rejouer cette ligne
                        </Button>
                      </CapabilityGuard>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground mt-2">
                  Aucune ligne ne nécessite une relance.
                </p>
              )}
            </div>

            <ReasonTextarea value={reason} onChange={(event) => setReason(event.target.value)} />

            {/* Rejeu automatique (C-18) — logique côté backend, reflet informatif ici. */}
            <div className="text-quipux-blue-800 border-quipux-blue-200 bg-quipux-blue-50 rounded-lg border p-3 text-xs">
              <p className="font-medium">Rejeu automatique</p>
              <p className="mt-1 leading-4">
                En cas d'échec, un rejeu automatique s'exécute chaque minute pendant 10 minutes.
                Au-delà, la ligne est escaladée à l'agent pour un rejeu manuel.
              </p>
            </div>
          </div>

          <SheetFooter className="border-border border-t p-5">
            <CapabilityGuard capability="payment:replay">
              <SheetClose asChild>
                <Button
                  variant="destructive"
                  disabled={replayableLines.length === 0 || reason.trim().length < 10}
                  onClick={() =>
                    toast.success('Rejeu programmé', {
                      description: `Motif journalisé : ${reason.trim()}`,
                    })
                  }
                >
                  <RotateCcw className="size-4" />
                  Relancer l'enregistrement
                </Button>
              </SheetClose>
            </CapabilityGuard>
            <SheetClose asChild>
              <Button variant="outline">Annuler</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={lineDetailSheetOpen} onOpenChange={setLineDetailSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Détail de la ligne de paiement</SheetTitle>
            <SheetDescription>
              {selectedLine ? `${selectedLine.liquidationNumber} · plaque ${selectedLine.plate}` : 'Ligne de liquidation'}
            </SheetDescription>
          </SheetHeader>

          {selectedLine ? (
            <div className="grid gap-4 p-5">
              <div className="rounded-lg border border-border p-4">
                <p className="text-muted-foreground text-xs uppercase">Code de paiement</p>
                <div className="mt-2">
                  <LiquidationNumber value={selectedLine.liquidationNumber} copyable />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-4">
                  <p className="text-muted-foreground text-xs uppercase">Plaque</p>
                  <p className="text-quipux-navy-950 mt-2 font-semibold">{selectedLine.plate}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-muted-foreground text-xs uppercase">Montant</p>
                  <div className="mt-2">
                    <Amount value={selectedLine.amount} />
                  </div>
                </div>
              </div>

              <div
                className={
                  selectedLineCanReplay
                    ? 'rounded-lg border border-danger/25 bg-danger/5 p-4'
                    : 'rounded-lg border border-status-paid/25 bg-status-paid/5 p-4'
                }
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase">Enregistrement Quipux</p>
                    <div className="mt-2">
                      <StatusBadge kind="sgi" status={selectedLine.sgiStatus} compact />
                    </div>
                  </div>
                  <p className="text-muted-foreground max-w-[12rem] text-right text-sm">
                    {selectedLineCanReplay
                      ? 'Cette ligne est à reprendre dans la relance d’enregistrement Quipux du paiement.'
                      : 'Aucune relance d’enregistrement Quipux n’est attendue pour cette ligne.'}
                  </p>
                </div>
                {selectedLine.failureReason ? (
                  <div className="border-danger/20 mt-3 border-t pt-3">
                    <p className="text-muted-foreground text-xs uppercase">Motif d’échec</p>
                    <p className="text-quipux-navy-950 mt-1 text-sm">
                      <code className="text-status-failed-800 font-mono text-xs">
                        {selectedLine.failureCode}
                      </code>{' '}
                      {selectedLine.failureReason}
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-border p-4 text-sm">
                <p className="text-muted-foreground text-xs uppercase">Paiement parent</p>
                <p className="text-quipux-navy-950 mt-2 break-all font-mono">
                  {payment.merchantReference}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusBadge kind="payment" status={payment.paymentStatus} compact />
                  <StatusBadge kind="sgi" status={payment.sgiStatus} compact />
                  <span className="text-muted-foreground">{formatDateTime(payment.updatedAt)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <EmptyState title="Ligne introuvable" description="La ligne sélectionnée n'est plus disponible." />
            </div>
          )}

          <SheetFooter className="border-border border-t p-5">
            {selectedLine && selectedLineCanReplay ? (
              <CapabilityGuard capability="payment:replay">
                <SheetClose asChild>
                  <Button variant="destructive" onClick={() => replayLine(selectedLine)}>
                    <RotateCcw className="size-4" />
                    Rejouer cette ligne
                  </Button>
                </SheetClose>
              </CapabilityGuard>
            ) : null}
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
